/**
 * useTenantPropertyType — reads the tenant's `tenant_settings.property_type`
 * setting and returns it for the adaptive hierarchy navigation in
 * `RoomReadinessBoard` (PROJECT_STATE task #64).
 *
 * Three values, mirroring the public.property_type ENUM and the landing
 * page's vertical chips:
 *
 *   dorm           — campus → building → floor → room (full hierarchy)
 *   office         — building → floor → room          (no campus)
 *   property_mgmt  — building → room                  (no campus, no floor)
 *
 * Defaults to 'dorm' while the row is loading so the UI does not flash a
 * smaller picker set and then expand. The migration default is also
 * 'dorm', so a blank/unscoped fetch lands on the same answer.
 *
 * RLS scopes the query to the caller's organization — no tenant_id arg.
 */

import { useQuery } from '@tanstack/react-query'
import { useSupabase } from './useSupabase'

export type PropertyType = 'dorm' | 'office' | 'property_mgmt'

export const DEFAULT_PROPERTY_TYPE: PropertyType = 'dorm'

interface UseTenantPropertyTypeResult {
  propertyType: PropertyType
  isLoading:    boolean
}

export function useTenantPropertyType(): UseTenantPropertyTypeResult {
  const supabase = useSupabase()

  const { data, isLoading } = useQuery({
    queryKey:  ['tenant-property-type'],
    queryFn:   async (): Promise<PropertyType> => {
      const { data, error } = await supabase
        .from('tenant_settings')
        .select('property_type')
        .single()

      if (error) throw error
      const raw = (data as { property_type?: string } | null)?.property_type
      if (raw === 'dorm' || raw === 'office' || raw === 'property_mgmt') return raw
      return DEFAULT_PROPERTY_TYPE
    },
    staleTime: 5 * 60 * 1000,
    retry:     2,
  })

  return {
    propertyType: data ?? DEFAULT_PROPERTY_TYPE,
    isLoading,
  }
}
